import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { AdminSidebar } from '../../components/AdminSidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Users, FileImage, ShoppingCart, DollarSign, RefreshCw } from 'lucide-react';
import { API_URL } from '../../utils/api';
import { getErrorMessage } from '../../utils/errors';
import { formatPrice } from '../../utils/format';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_URL}/admin/stats`);
      setStats(response.data);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not load dashboard stats.'));
    } finally {
      setLoading(false);
    }
  };

  const statCards = stats ? [
    { title: 'Total Users', value: stats.total_users ?? 0, icon: Users, color: 'text-blue-600', link: '/admin/customers' },
    { title: 'Templates', value: stats.total_templates || 0, icon: FileImage, color: 'text-purple-600', link: '/admin/templates' },
    { title: 'Total Orders', value: stats.total_orders ?? 0, icon: ShoppingCart, color: 'text-green-600', link: '/admin/orders' },
    { title: 'Revenue', value: formatPrice(stats.total_revenue || 0), icon: DollarSign, color: 'text-gold', link: '/admin/transactions' },
  ] : [];

  return (
    <div className="min-h-screen bg-paper">
      <AdminSidebar />
      
      <div className="ml-64 p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="font-serif text-3xl font-bold text-ink mb-2">Admin Dashboard</h1>
          <p className="text-ink/60 mb-8">Overview of your Azamein platform</p>

          {loading ? (
            <div className="flex justify-center py-20" role="status">
              <div className="animate-spin rounded-full h-12 w-12 border-2 border-gold/20 border-t-gold"></div>
              <span className="sr-only">Loading stats</span>
            </div>
          ) : error ? (
            <div className="bg-white border border-dashed border-border rounded-lg p-10 text-center" role="alert">
              <p className="text-ink/70 mb-4">{error}</p>
              <Button variant="outline" onClick={fetchStats}>
                <RefreshCw className="h-4 w-4" />
                Try again
              </Button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-6">
              {statCards.map((stat, index) => (
                <motion.div
                  key={stat.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link to={stat.link} className="block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold">
                    <Card className="stats-card hover:shadow-lg transition-shadow cursor-pointer">
                        <CardHeader className="flex flex-row items-center justify-between pb-2">
                        <CardTitle className="text-sm font-medium text-ink/60">
                            {stat.title}
                        </CardTitle>
                        <stat.icon className={`h-5 w-5 ${stat.color}`} />
                        </CardHeader>
                        <CardContent>
                        <div className="text-3xl font-bold text-ink" data-testid={`stat-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}>
                            {stat.value}
                        </div>
                        </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
